import { RootState } from 'redux/schemas';
import { createSelector } from 'reselect';
import { values } from 'lodash';
import { AnalyticsPageType, AverageChangeRow, ConfigurationType, QuantileDistribution } from 'redux/schemas/app/analytics';
import { find } from 'underscore';
import { extractTextFromHTML } from 'shared/utils';
import { getCurrentCourse } from './course';

const userEnrollments = (state: RootState) => state.models.enrollments;
const allCourses = (state: RootState) => state.models.courses;
const accessibleCourses = (state: RootState) => state.app.analytics.accessibleCourses || [];

const getUserEnrollments = createSelector(
  [userEnrollments, allCourses],
  (enrollments, courses) => values(enrollments)
    .map((enrollment) => {
      const course = courses[enrollment.courseCatalogId];
      return {
        ...enrollment,
        name: course?.name,
        catalogId: enrollment.courseCatalogId,
        isPrimary: course?.isPrimary,
        isCohort: course?.primaryExists,
      };
    })
    .filter(enrollment => enrollment.isInstructorOrReportAdmin)
    .sort((a, b) => {
      const aDate = a.lastActivity || a.enrolledAt;
      const bDate = b.lastActivity || b.enrolledAt;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    }),
);

const getAccessiblePrimaryCourses = createSelector(
  [accessibleCourses],
  (courses) => courses.map((course) => ({
    ...course,
    isPrimary: true,
  })),
);

export const getCourseOptions = createSelector(
  [
    getUserEnrollments,
    getAccessiblePrimaryCourses,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
  ],
  (enrollments, primaryCourses, analyticsPageType) => (analyticsPageType === AnalyticsPageType.COURSE_ANALYTICS ? enrollments : primaryCourses),
);

export const getQuestionSets = createSelector(
  [
    (state: RootState) => state.app.analytics.questionSets,
    (state: RootState) => state.app.currentCatalogId,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
    (_state: RootState, _analyticsPageType: AnalyticsPageType, catalogId?: string) => catalogId,
  ],
  (questionSets, currentCatalogId, analyticsPageType, catalogId) => questionSets?.[catalogId ?? currentCatalogId]?.[analyticsPageType] || {},
);

export const getSurveyQuestionSets = createSelector(
  [
    (state: RootState) => state.app.analytics.surveyQuestionsets,
    (state: RootState) => state.app.currentCatalogId,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
    (_state: RootState, _analyticsPageType: AnalyticsPageType, catalogId?: string) => catalogId,
  ],
  (
    questionSets,
    currentCatalogId,
    analyticsPageType,
    catalogId,
  ) => questionSets?.[catalogId ?? currentCatalogId]?.[analyticsPageType]?.surveys || [],
);

export const showPrePostTab = createSelector(
  [
    getCurrentCourse,
    (state: RootState) => state.app.analytics.surveyQuestionsets,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
  ],
  (currentCourse, questionSets, analyticsPageType) => {
    const catalogId = currentCourse?.catalogId;

    const questionSetData = questionSets?.[catalogId]?.[analyticsPageType] || {};
    const surveyQuestionSets = questionSetData.surveys || [];
    return surveyQuestionSets.length > 0;
  },
);

export const getSelectedCourse = createSelector(
  [
    allCourses,
    getAccessiblePrimaryCourses,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,
    (_state: RootState, _analyticsPageType: AnalyticsPageType, selectedCatalogId?: string) => selectedCatalogId,
  ],
  (courses, primaryCourses, analyticsPageType, selectedCatalogId) => {
    if (analyticsPageType === AnalyticsPageType.COURSE_ANALYTICS) {
      return courses[selectedCatalogId];
    }
    return primaryCourses.find(course => course.catalogId === selectedCatalogId);
  },
);

export const getExistingConfiguration = createSelector(
  [
    (state: RootState) => state.app.analytics.existingConfiguration,
    (state: RootState, _analyticsPageType: AnalyticsPageType) => state.app.currentCatalogId,
    (_state: RootState, analyticsPageType: AnalyticsPageType) => analyticsPageType,

  ],
  (existingConfiguration, catalogId, _analyticsPageType) => find(
    existingConfiguration?.[catalogId] || [], (config) => (
      config.configurationType === ConfigurationType[_analyticsPageType]
    ),
  ),
);

export const getAverageChange = createSelector(
  [
    (state: RootState) => state.app.analytics.distributionCharts,
    (state: RootState) => state.app.analytics.currentPrePostConfigurationId,
  ],
  (distributionCharts, configurationId): AverageChangeRow[] => {
    if (!configurationId) {
      return [];
    }
    const distributionData = distributionCharts?.[configurationId];
    if (!distributionData) {
      return [];
    }

    return Object.entries(distributionData).map(([key, dist]) => {
      const isInvalid = (dist.pre?.average === null
        || dist.pre?.average === undefined)
      && (dist.post?.average === null
        || dist.post?.average === undefined
      );

      const averageChangeData = {
        id: key,
        question: dist.questionText ? extractTextFromHTML(dist.questionText) : '',
        preAverage: dist.pre?.average,
        postAverage: dist.post?.average,
        changePercent: null,
      };
      if (isInvalid) {
        // If all the values are invalid, return as is
        // The state handled on UI side
        return averageChangeData;
      }
      if (!isInvalid) {
        // to avoid division by zero
        if (!dist.pre?.average) {
          averageChangeData.changePercent = 0;
          return averageChangeData;
        }
        const postAvg = dist.post?.average || 0;
        const change = ((postAvg - dist.pre.average) / dist.pre.average) * 100;
        const changePercent = parseFloat(change.toFixed(2));
        averageChangeData.changePercent = changePercent;
      }
      return averageChangeData;
    });
  },
);

export const getQuantilesDistribution = createSelector(
  [
    (state: RootState) => state.app.analytics.distributionCharts,
    (state: RootState) => state.app.analytics.currentPrePostConfigurationId,
  ],
  (distributionCharts, configurationId): QuantileDistribution[] => {
    if (!configurationId) {
      return [];
    }
    const distributionData = distributionCharts?.[configurationId];
    if (!distributionData) {
      return [];
    }

    return Object.entries(distributionData).map(([key, dist]) => ({
      id: key,
      question: dist.questionText ? extractTextFromHTML(dist.questionText) : '',
      preDistribution: {
        low: dist.pre?.quantiles?.[0] ?? 0,
        medium: dist.pre?.quantiles?.[1] ?? 0,
        high: dist.pre?.quantiles?.[2] ?? 0,
      },
      postDistribution: {
        low: dist.post?.quantiles?.[0] ?? 0,
        medium: dist.post?.quantiles?.[1] ?? 0,
        high: dist.post?.quantiles?.[2] ?? 0,
      },
    }));
  },
);

export const shouldReloadCharts = createSelector(
  [
    (state: RootState) => state.app.analytics.surveyCharts,
    (state: RootState) => state.app.analytics.currentPrePostConfigurationId,
  ],
  (surveyCharts, configurationId) => (configurationId ? surveyCharts?.[configurationId]?.shouldReload : false),
);
